import { useEffect, useRef } from 'react'

/** True when focus is in a text field, so we shouldn't hijack typing keys. */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
}

/** True when the keystroke originated inside the phone panel subtree. */
export function isWithinPhonePanel(target: EventTarget | null): boolean {
  return (
    target instanceof Element && target.closest('.phone-panel-surface') !== null
  )
}

/**
 * Nav keys apply when focus is inside the panel (incl. the search box) or when
 * nothing in particular is focused — but never while the user types elsewhere.
 */
function inNavScope(target: EventTarget | null): boolean {
  return (
    isWithinPhonePanel(target) || target === document.body || target === null
  )
}

export interface PhonePanelKeyboard {
  /** Whole feature gate — the panel must be open. */
  enabled: boolean
  /** True only on the main list view (enables tab + item navigation). */
  onList: boolean
  /** Navigable row count; 0 disables up/down + Enter (e.g. voicemail). */
  itemCount: number
  onMoveActive: (delta: number) => void
  onCycleFilter: (dir: 1 | -1) => void
  /** Open the currently highlighted row (no-op when nothing is highlighted). */
  onOpenActive: () => void
}

const MOVE_DELTA: Record<string, number> = { ArrowUp: -1, ArrowDown: 1 }
const FILTER_DELTA: Record<string, 1 | -1> = { ArrowLeft: -1, ArrowRight: 1 }

function handleListKey(event: KeyboardEvent, h: PhonePanelKeyboard) {
  const { key } = event
  if (key in MOVE_DELTA) {
    if (h.itemCount === 0) return
    event.preventDefault()
    h.onMoveActive(MOVE_DELTA[key])
  } else if (key in FILTER_DELTA) {
    // ←/→ always switch tabs on the list — the only field here is the search
    // box, where tab cycling is wanted over caret movement.
    event.preventDefault()
    h.onCycleFilter(FILTER_DELTA[key])
  } else if (key === 'Enter') {
    if (h.itemCount === 0) return
    event.preventDefault()
    h.onOpenActive()
  }
}

/**
 * Global list navigation for the phone panel: ↑/↓ move the cursor, ←/→ switch
 * tabs, Enter opens the highlighted row. Escape is handled separately via the
 * shared escape stack. A ref keeps the single window listener reading the
 * latest state without re-subscribing.
 */
export function usePhonePanelKeyboard(handlers: PhonePanelKeyboard) {
  const ref = useRef(handlers)
  ref.current = handlers

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const h = ref.current
      // Only drive navigation on the list view, scoped so it never hijacks
      // arrows/Enter while the user works in the main app beside the panel.
      if (!h.enabled || !h.onList || !inNavScope(event.target)) return
      handleListKey(event, h)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
